import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Bell, PlusCircle, Settings, Shield, User, FileText, Loader2, X, Check } from "lucide-react";
import { useAuth } from "../Auth/AuthContext";
import { useNavigate } from "react-router-dom";

const AdminSettings = ({ admin, darkMode, toggleDarkMode }) => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://localhost:44333/api/';

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setUploadError(null);
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setUploadError("Please select a file.");
            return;
        }

        // Validate file type
        const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(selectedFile.type)) {
            setUploadError("Please upload a PDF or Word document.");
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("userId", "admin");

        try {
            const response = await fetch(`${baseUrl}Agreement/Upload`, {
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                setUploadSuccess(true);
                setTimeout(() => {
                    setIsModalOpen(false);
                    setSelectedFile(null);
                    setUploadSuccess(false);
                }, 1500);
            } else {
                const errorText = await response.text();
                setUploadError(errorText || "Error uploading file");
            }
        } catch (error) {
            console.error("Upload error:", error);
            setUploadError("Network error occurred while uploading.");
        } finally {
            setIsUploading(false);
        }
    };

    const settings = [
        {
            title: "Notifications",
            description: "Manage your notification preferences",
            icon: Bell,
            action: () => setNotifications(!notifications),
            isToggle: true,
            active: notifications
        },
        {
            title: "Manage Users",
            description: "Add, remove, or update user information",
            icon: User,
            action: () => navigate("/publishers"),
            isToggle: false
        },
        {
            title: "Agreement Settings",
            description: "Update publisher agreement document",
            icon: FileText,
            action: () => setIsModalOpen(true),
            isToggle: false
        },
        {
            title: "Ad Units",
            description: "Configure ad units and placements",
            icon: PlusCircle,
            action: () => navigate("/adunits"),
            isToggle: false
        },
        {
            title: "Ads.txt",
            description: "Manage your Ads.txt file for monetization",
            icon: Shield,
            action: () => navigate("/AdsTxtConfiguration"),
            isToggle: false
        },
        {
            title: "Ad Code",
            description: "Customize your ad code configurations",
            icon: Settings,
            action: () => navigate("/AdsCodeConfiguration"),
            isToggle: false
        },
        {
            title: "Appearance",
            description: "Change theme between light and dark mode",
            icon: darkMode ? Sun : Moon,
            action: toggleDarkMode,
            isToggle: false
        }
    ];

    return (
        <div className={`min-h-screen ${darkMode ? "bg-gray-900" : "bg-gray-50"}`}>
            <div className="container mx-auto px-4 py-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className={`p-6 max-w-6xl mx-auto rounded-xl shadow-lg ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"}`}
                >
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">Admin Settings</h2>
                        <button 
                            onClick={toggleDarkMode}
                            className={`p-2 rounded-full ${darkMode ? "bg-gray-700 text-yellow-300" : "bg-gray-200 text-gray-700"}`}
                            aria-label="Toggle dark mode"
                        >
                            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {settings.map(({ title, description, icon: Icon, action, isToggle, active }, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`p-4 rounded-lg transition-all cursor-pointer flex items-start gap-4 ${darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-50 hover:bg-gray-100"} border ${darkMode ? "border-gray-600" : "border-gray-200"}`}
                                onClick={action}
                                role="button"
                                tabIndex="0"
                                onKeyDown={(e) => e.key === 'Enter' && action()}
                            >
                                <div className={`p-3 rounded-full ${darkMode ? "bg-gray-600" : "bg-gray-200"}`}>
                                    <Icon size={20} className={isToggle && active ? "text-green-500" : darkMode ? "text-gray-300" : "text-gray-600"} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold">{title}</h3>
                                    <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-500"}`}>{description}</p>
                                </div>
                                {isToggle && (
                                    <motion.div 
                                        className={`w-12 h-6 rounded-full p-1 flex items-center ${active ? "justify-end bg-green-500" : "justify-start bg-gray-400"}`}
                                        initial={false}
                                        animate={{ backgroundColor: active ? "#10B981" : "#9CA3AF" }}
                                    >
                                        <motion.div 
                                            className="w-4 h-4 bg-white rounded-full shadow-md"
                                            layout
                                            transition={{ type: "spring", stiffness: 700, damping: 30 }}
                                        />
                                    </motion.div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>

            <AnimatePresence>
                {isModalOpen && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <motion.div 
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className={`rounded-xl shadow-xl w-full max-w-md ${darkMode ? "bg-gray-800" : "bg-white"}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-xl font-semibold">Upload Agreement</h2>
                                    <button 
                                        onClick={() => setIsModalOpen(false)}
                                        className={`p-1 rounded-full ${darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                                        aria-label="Close modal"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                
                                <div className="mb-4">
                                    <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                                        Select Document (PDF or Word)
                                    </label>
                                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${darkMode ? "border-gray-600 bg-gray-700" : "border-gray-300 bg-gray-50"} transition-colors ${selectedFile ? (darkMode ? "border-green-500" : "border-green-400") : ""}`}>
                                        {selectedFile ? (
                                            <div className="flex flex-col items-center">
                                                <Check className="text-green-500 mb-2" size={24} />
                                                <p className="font-medium">{selectedFile.name}</p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {(selectedFile.size / 1024).toFixed(2)} KB
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <FileText className={`mx-auto mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`} size={24} />
                                                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                                                    Drag & drop file here or click to browse
                                                </p>
                                            </>
                                        )}
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            id="file-upload"
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            className={`mt-4 inline-block px-4 py-2 rounded-md cursor-pointer ${darkMode ? "bg-gray-600 hover:bg-gray-500 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                                        >
                                            Choose File
                                        </label>
                                    </div>
                                </div>
                                
                                {uploadError && (
                                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                                        {uploadError}
                                    </div>
                                )}
                                
                                {uploadSuccess && (
                                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm flex items-center gap-2">
                                        <Check size={16} />
                                        Agreement uploaded successfully!
                                    </div>
                                )}
                                
                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            setSelectedFile(null);
                                            setUploadError(null);
                                        }}
                                        className={`px-4 py-2 rounded-md ${darkMode ? "bg-gray-600 hover:bg-gray-500 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                                        disabled={isUploading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!selectedFile || isUploading}
                                        className={`px-4 py-2 rounded-md flex items-center gap-2 ${(!selectedFile || isUploading) ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"} text-white`}
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="animate-spin" size={18} />
                                                Uploading...
                                            </>
                                        ) : (
                                            "Upload"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminSettings;